import tokens from '@kluein/design-tokens';
import { Text, Box, Button, TextInput } from '@kluein/klue-ui';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAskKlueFeedback } from 'pages/dashboard/hooks';
import { setAskKlueHasFilledDefaultFeedback } from 'store/views/dashboard';
import TEST_IDS from 'test-ids';
import TRACKING_IDS from 'tracking-ids';

function Feedback() {
  const { t } = useTranslation(['Dashboard', 'Common']);
  const [showFeedbackForm, setShowFeedbackForm] = useState<boolean>(false);
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);
  const submitFeedback = useAskKlueFeedback();

  const handleClickYes = () => {
    submitFeedback({
      isHelpful: true,
    });
    setIsFormSubmitted(true);
    setAskKlueHasFilledDefaultFeedback(true);
  };

  const handleSubmit = useCallback(() => {
    submitFeedback({
      isHelpful: false,
      comment: feedbackText,
    });
    setIsFormSubmitted(true);
    setAskKlueHasFilledDefaultFeedback(true);
  }, [submitFeedback, feedbackText]);

  const handleDismiss = useCallback(() => {
    setIsFormSubmitted(true);
    setAskKlueHasFilledDefaultFeedback(true);
  }, []);

  if (isFormSubmitted) {
    return (
      <Box align="center">
        <Text
          color={tokens.color.neutral[900]}
          fontFamily={tokens.fontFamily.Proxima}
          size={tokens.fontSize.bodyFont.small}
          fontWeight="semibold"
          lineHeight={tokens.lineHeight.xxdense}
        >
          {t('Dashboard:askKlue.answerModal.feedbackSubmitted')}
        </Text>
      </Box>
    );
  }

  if (showFeedbackForm) {
    return (
      <Box
        direction="row"
        align="center"
        gap="xsmall"
        flex={{ grow: 1 }}
        width={{ min: '330px' }}
      >
        <TextInput
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={true}
          size="small"
          title={t('Dashboard:askKlue.answerModal.feedbackPlaceholder')}
          onChange={(e) => setFeedbackText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && feedbackText?.trim()) {
              e.preventDefault();
              handleSubmit();
            }
          }}
          value={feedbackText}
          placeholder={t('Dashboard:askKlue.answerModal.feedbackPlaceholder')}
          data-test-id={TEST_IDS.askKlue.feedback.textInput}
        />
        <Box direction="row" align="center" gap="xxsmall" flex={{ grow: 1 }}>
          <Button
            variant="flat"
            size="small"
            label={t('Common:dismiss')}
            onClick={handleDismiss}
            data-tracking-id={TRACKING_IDS.askKlue.feedback.dismissButton}
          />
          <Button
            variant="secondary"
            size="small"
            label={t('Common:actions.submit')}
            onClick={handleSubmit}
            disabled={!feedbackText?.trim()}
            data-tracking-id={TRACKING_IDS.askKlue.feedback.submitButton}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box direction="row" align="center" gap="small">
      <Text
        color={tokens.color.neutral[800]}
        fontFamily={tokens.fontFamily.Proxima}
        size={tokens.fontSize.bodyFont.small}
        fontWeight="semibold"
        lineHeight={tokens.lineHeight.xxdense}
      >
        {t('Dashboard:askKlue.answerModal.copyFeedbackYesNo')}
      </Text>
      <Box direction="row" align="center" gap="xxsmall" flex={false}>
        <Button
          variant="flat"
          size="small"
          label={t('Common:yes')}
          onClick={handleClickYes}
          data-tracking-id={TRACKING_IDS.askKlue.feedback.yesButton}
        />
        <Button
          variant="flat"
          size="small"
          label={t('Common:no')}
          onClick={() => setShowFeedbackForm(true)}
          data-tracking-id={TRACKING_IDS.askKlue.feedback.noButton}
        />
      </Box>
    </Box>
  );
}

type AskKlueModalFooterProps = {
  feedbackEnabled?: boolean;
  isInitialScreen?: boolean;
  isPage?: boolean;
};
function AskKlueModalFooter({
  feedbackEnabled = false,
  isInitialScreen = false,
  isPage = false,
}: AskKlueModalFooterProps) {
  const { t } = useTranslation();

  if (isPage) {
    return null;
  }

  return (
    <Box
      direction="row"
      justify={isInitialScreen ? 'center' : 'between'}
      pad={{
        bottom: tokens.spacing.medium,
        left: tokens.spacing.small,
      }}
      align="center"
      gap="xxsmall"
    >
      <Box>
        <Text
          color={tokens.color.secondary[600]}
          fontFamily={tokens.fontFamily.Inter}
          size={tokens.fontSize.bodyFont.small}
          fontWeight="medium"
          lineHeight={tokens.lineHeight.xxdense}
        >
          {t('Dashboard:askKlue.answerModal.copyBottom')}
        </Text>
      </Box>
      {feedbackEnabled && !isInitialScreen && (
        <Box>
          <Feedback />
        </Box>
      )}
    </Box>
  );
}

export default AskKlueModalFooter;
